"use client";

import {
  VALUE_PROP_MATH_MAX_CONCURRENT,
  VALUE_PROP_MATH_SPAWN_MAX_MS,
  VALUE_PROP_MATH_SPAWN_MIN_MS,
  VALUE_PROP_MATH_SPAWN_RADIUS_FRACTION,
  VALUE_PROP_MATH_UNIFORM_SCALE,
  type ValuePropMathParticle,
  createValuePropMathParticle,
  randIntBetween,
} from "@/components/about/value-prop-math-burst-utils";
import { type AnimationEvent, type CSSProperties, useEffect, useRef, useState } from "react";

type ValuePropMathBurstProps = {
  /** When true, continuously spawn glyphs from behind the Rive center. */
  active: boolean;
};

/**
 * Decorative math glyphs that radiate from the center while PlayMath is selected.
 * Painted behind the Rive layers; disabled under prefers-reduced-motion.
 */
export function ValuePropMathBurst({ active }: ValuePropMathBurstProps) {
  const [enabled, setEnabled] = useState(false);
  const [particles, setParticles] = useState<ValuePropMathParticle[]>([]);
  const idRef = useRef(0);
  const spawnTimeoutRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const activeRef = useRef(active);

  useEffect(() => {
    activeRef.current = active;
    if (!active) {
      setParticles([]);
      if (spawnTimeoutRef.current != null) {
        window.clearTimeout(spawnTimeoutRef.current);
        spawnTimeoutRef.current = null;
      }
    }
  }, [active]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setEnabled(!mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      pausedRef.current = document.hidden;
    };
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (!enabled || !active) return;

    const scheduleNext = () => {
      if (spawnTimeoutRef.current != null) {
        window.clearTimeout(spawnTimeoutRef.current);
      }
      const wait = randIntBetween(VALUE_PROP_MATH_SPAWN_MIN_MS, VALUE_PROP_MATH_SPAWN_MAX_MS);
      spawnTimeoutRef.current = window.setTimeout(() => {
        spawnTimeoutRef.current = null;
        if (!activeRef.current || pausedRef.current) {
          scheduleNext();
          return;
        }
        setParticles((current) => {
          if (current.length >= VALUE_PROP_MATH_MAX_CONCURRENT) return current;
          const next = createValuePropMathParticle(idRef.current++);
          return [...current, next];
        });
        scheduleNext();
      }, wait);
    };

    // Kick a few immediately so PlayMath feels lively on select.
    setParticles((current) => {
      if (current.length > 0) return current;
      const burst = Array.from({ length: 4 }, () => createValuePropMathParticle(idRef.current++));
      return burst;
    });
    scheduleNext();

    return () => {
      if (spawnTimeoutRef.current != null) {
        window.clearTimeout(spawnTimeoutRef.current);
        spawnTimeoutRef.current = null;
      }
    };
  }, [active, enabled]);

  const handleAnimationEnd = (event: AnimationEvent<HTMLImageElement>, id: number) => {
    // Ignore bubbled ends from unexpected child animations; our imgs only run this one.
    if (event.animationName && event.animationName !== "value-prop-math-burst") return;
    setParticles((current) => current.filter((particle) => particle.id !== id));
  };

  if (!enabled || !active) {
    return null;
  }

  return (
    <div
      className="pointer-events-none absolute inset-0 z-0 overflow-visible"
      data-testid="value-prop-math-burst"
      aria-hidden="true"
    >
      {particles.map((particle) => {
        const style = {
          "--vp-math-sx": `${particle.dx * VALUE_PROP_MATH_SPAWN_RADIUS_FRACTION}px`,
          "--vp-math-sy": `${particle.dy * VALUE_PROP_MATH_SPAWN_RADIUS_FRACTION}px`,
          "--vp-math-dx": `${particle.dx}px`,
          "--vp-math-dy": `${particle.dy}px`,
          "--vp-math-scale": String(VALUE_PROP_MATH_UNIFORM_SCALE),
          width: "auto",
          height: "auto",
          animation: `value-prop-math-burst ${particle.durationMs}ms linear forwards`,
        } as CSSProperties;

        return (
          <img
            key={particle.id}
            src={particle.src}
            alt=""
            draggable={false}
            data-testid="value-prop-math-particle"
            data-math-src={particle.src}
            data-math-scale={VALUE_PROP_MATH_UNIFORM_SCALE}
            className="absolute left-1/2 top-1/2 max-w-none select-none"
            style={style}
            onAnimationEnd={(event) => handleAnimationEnd(event, particle.id)}
          />
        );
      })}
    </div>
  );
}
